import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Upload, FileText, Trash2, Download, Image as ImageIcon } from "lucide-react";
import { formatDateTimeFr } from "@/lib/format";
import { toast } from "sonner";

export default function BuildingDocumentsTab({ building, onlyImages = false }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/documents?resource_type=building&resource_id=${building.id}`);
      let items = data.items || [];
      if (onlyImages) items = items.filter((d) => (d.content_type || "").startsWith("image/"));
      setDocs(items);
    } catch (err) {
      console.error("Load documents failed:", err);
    }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [building.id, onlyImages]);

  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      await api.post(
        `/documents/upload?resource_type=building&resource_id=${building.id}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      toast.success(onlyImages ? "Photo ajoutée" : "Document ajouté"); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Envoi impossible"); }
    setUploading(false); e.target.value = "";
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer ce document ?")) return;
    try { await api.delete(`/documents/${id}`); toast.success("Supprimé"); load(); }
    catch { toast.error("Suppression impossible"); }
  };

  const download = async (d) => {
    try {
      const resp = await api.get(`/documents/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a"); a.href = url; a.download = d.original_filename; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Téléchargement impossible"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-overline">{onlyImages ? "Photothèque" : "Coffre-fort"}</div>
          <h3 className="mt-1 font-heading text-xl">{onlyImages ? "Photos du bâtiment" : "Documents du bâtiment"}</h3>
        </div>
        <div>
          <input ref={inputRef} type="file" className="hidden" onChange={upload}
                 accept={onlyImages ? "image/*" : ".pdf,.png,.jpg,.jpeg,.webp,.docx,.xlsx,.csv,.txt"} />
          <Button className="btn-pill" onClick={() => inputRef.current?.click()} disabled={uploading}
                  data-testid={onlyImages ? "building-photo-upload" : "building-doc-upload"}>
            <Upload className="mr-2 h-4 w-4" /> {uploading ? "Envoi…" : (onlyImages ? "Ajouter une photo" : "Ajouter un document")}
          </Button>
        </div>
      </div>

      {loading && <div className="text-sm text-muted-foreground py-4">Chargement…</div>}
      {!loading && docs.length === 0 && (
        <div className="card-elevated p-12 text-center text-muted-foreground">
          {onlyImages
            ? <><ImageIcon className="mx-auto h-8 w-8 mb-3 text-muted-foreground/60" /> Aucune photo pour ce bâtiment.</>
            : <><FileText className="mx-auto h-8 w-8 mb-3 text-muted-foreground/60" /> Aucun document.</>}
        </div>
      )}

      {!loading && docs.length > 0 && (
        onlyImages ? (
          <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3">
            {docs.map((d) => (
              <div key={d.id} className="card-elevated overflow-hidden">
                <div className="aspect-[4/3] bg-secondary/50 flex items-center justify-center text-muted-foreground">
                  <ImageIcon className="h-8 w-8" />
                </div>
                <div className="p-3">
                  <div className="text-sm font-medium truncate">{d.original_filename}</div>
                  <div className="text-xs text-muted-foreground">{Math.round((d.size || 0) / 1024)} Ko · {formatDateTimeFr(d.created_at)}</div>
                  <div className="mt-2 flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => download(d)}><Download className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => del(d.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="card-elevated overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-secondary/40">
                <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="px-6 py-3">Nom</th><th className="px-6 py-3">Type</th>
                  <th className="px-6 py-3">Taille</th><th className="px-6 py-3">Ajouté le</th><th></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {docs.map((d) => (
                  <tr key={d.id} className="hover:bg-secondary/30">
                    <td className="px-6 py-3 flex items-center gap-2"><FileText className="h-4 w-4 text-muted-foreground" /><span className="truncate max-w-[320px]">{d.original_filename}</span></td>
                    <td className="px-6 py-3 text-xs font-mono text-muted-foreground">{d.content_type}</td>
                    <td className="px-6 py-3 text-muted-foreground">{Math.round((d.size || 0) / 1024)} Ko</td>
                    <td className="px-6 py-3 text-xs text-muted-foreground">{formatDateTimeFr(d.created_at)}</td>
                    <td className="px-6 py-3 text-right space-x-1">
                      <Button variant="ghost" size="sm" onClick={() => download(d)}><Download className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => del(d.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  );
}
